import React from 'react';
import { Text } from '@chakra-ui/react';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { FiTrash2 } from 'react-icons/fi';

interface DeleteConfirmationDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	message?: string;
	confirmText?: string;
	cancelText?: string;
	onConfirm: () => void;
	onCancel: () => void;
	isLoading?: boolean;
	loadingText?: string;
	itemName?: string;
	children?: React.ReactNode;
	/** Show the "This action cannot be undone." line. Off for reversible actions (e.g. cancelling a transfer). */
	irreversible?: boolean;
}

/**
 * Delete-specific wrapper around the shared confirmation dialog.
 */
const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
	isOpen,
	onOpenChange,
	title,
	message,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	onConfirm,
	onCancel,
	isLoading = false,
	loadingText = 'Processing...',
	itemName,
	children,
	irreversible = true,
}) => {
	return (
		<ConfirmationDialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={title}
			confirmText={confirmText}
			cancelText={cancelText}
			onConfirm={onConfirm}
			onCancel={onCancel}
			isLoading={isLoading}
			loadingText={loadingText}
			confirmIcon={<FiTrash2 />}
			confirmButtonColorPalette="red"
		>
			{itemName ? (
				<Text fontSize="md" color="fg.muted">
					Are you sure you want to delete "
					<Text fontStyle="italic" fontWeight="semibold" display="inline">
						{itemName}
					</Text>
					"?
				</Text>
			) : null}
			{/* Alongside the name, not instead of it. The two say different things — which thing is
			    going, and what else goes with it — and an `itemName` used to silently discard the
			    warning a caller had written. */}
			{message ? (
				<Text fontSize="md" color="fg.muted" whiteSpace="pre-line" mt={itemName ? 4 : 0}>
					{message}
				</Text>
			) : null}
			{irreversible && (
				<Text fontSize="md" color="fg.muted" mt={4}>
					This action cannot be undone.
				</Text>
			)}
			{children}
		</ConfirmationDialog>
	);
};

export default DeleteConfirmationDialog;
